import { useEffect, useState } from "react";
import LockerArrangement from "../LockerArangment/LockerArangment";
import { getLockers, updateLocker as updateLockerAPI, deleteLocker as deleteLockerAPI } from "../../api/axios";
import { useTranslation } from "react-i18next";

const Lockers = () => {
    const { t } = useTranslation('global');
    const [lockers, setLockers] = useState([]);
    const [lockerGroups, setLockerGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState('view'); // 'view', 'edit'

    const fetchLockers = async () => {
        try {
            setLoading(true);
            const response = await getLockers();

            // Group lockers by locker_group
            const groupedLockers = Object.entries(
                response.reduce((acc, locker) => {
                    const group = locker.locker_group || 'Ungrouped';
                    if (!acc[group]) acc[group] = [];
                    acc[group].push(locker);
                    return acc;
                }, {})
            ).map(([groupName, lockers]) => ({
                groupName,
                lockers: lockers.map(locker => ({
                    ...locker,
                    // Ensure all required fields exist
                    id: locker.id || Date.now() + Math.random(),
                    position_x: locker.position_x || 0,
                    position_y: locker.position_y || 0,
                    locker_serial: locker.locker_serial || '',
                    locker_pin: locker.locker_pin || '',
                    barrier_sn: locker.barrier_sn || '',
                    reader_serial_num: locker.reader_serial_num || '',
                    status: locker.status || 0
                }))
            }));

            setLockerGroups(groupedLockers);

            // Set initial selection
            if (groupedLockers.length > 0) {
                setSelectedGroup("all");

                // sve grupe odmah spajamo u jednu listu sa offset-om
                let totalYOffset = 0;
                const gap = 1;
                const allLockers = groupedLockers.flatMap(group => {
                    const minY = Math.min(...group.lockers.map(l => l.position_y));
                    const lockersWithOffset = group.lockers.map(locker => ({
                        ...locker,
                        position_y: locker.position_y - minY + totalYOffset
                    }));

                    const maxY = Math.max(...group.lockers.map(l => l.position_y));
                    const groupHeight = maxY - minY;

                    totalYOffset += groupHeight + gap;

                    return lockersWithOffset;
                });

                setLockers(allLockers);
            }
        } catch (err) {
            setError('Failed to fetch lockers');
            console.error('Error fetching lockers:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleGroupChange = (e) => {
        const groupName = e.target.value;
        setSelectedGroup(groupName);

        if (groupName === "all") {
            // Crtaj sve grupe jedna ispod druge
            let totalYOffset = 0;
            const gap = 1; // razmak između grupa

            const allLockers = lockerGroups.flatMap(group => {
                const minY = Math.min(...group.lockers.map(l => l.position_y));

                const lockersWithOffset = group.lockers.map(locker => ({
                    ...locker,
                    position_y: locker.position_y - minY + totalYOffset
                }));

                const maxY = Math.max(...group.lockers.map(l => l.position_y));
                const groupHeight = maxY - minY;

                totalYOffset += groupHeight + gap;

                return lockersWithOffset;
            });

            setLockers(allLockers);
        } else {
            const selected = lockerGroups.find(g => g.groupName === groupName);
            setLockers(selected ? selected.lockers : []);
        }

        setMode('view'); // reset mode kad se menja grupa
    };



    const handleModeChange = (newMode) => {
        setMode(newMode);
    };

    const handleLockerUpdate = async (locker) => {
        try {
            await updateLockerAPI(locker.id, locker);
            // Update local state
            setLockers(prev => prev.map(l => l.id === locker.id ? locker : l));
            // Update lockerGroups state
            setLockerGroups(prev => prev.map(group => ({
                ...group,
                lockers: group.groupName === selectedGroup
                    ? group.lockers.map(l => l.id === locker.id ? locker : l)
                    : group.lockers
            })));
        } catch (error) {
            throw error;
        }
    };

    const handleLockerDelete = async (lockerId) => {
        try {
            await deleteLockerAPI(lockerId);
            // Update local state
            setLockers(prev => prev.filter(l => l.id !== lockerId));
            // Update lockerGroups state
            setLockerGroups(prev => prev.map(group => ({
                ...group,
                lockers: group.groupName === selectedGroup
                    ? group.lockers.filter(l => l.id !== lockerId)
                    : group.lockers
            })).filter(group => group.lockers.length > 0)); // Remove empty groups

            // If current group becomes empty, select first available group
            const updatedGroup = lockerGroups.find(g => g.groupName === selectedGroup);
            if (!updatedGroup || updatedGroup.lockers.length <= 1) {
                const remainingGroups = lockerGroups.filter(g =>
                    g.groupName !== selectedGroup || g.lockers.length > 1
                );
                if (remainingGroups.length > 0) {
                    setSelectedGroup(remainingGroups[0].groupName);
                    setLockers(remainingGroups[0].lockers);
                } else {
                    setSelectedGroup('');
                    setLockers([]);
                }
            }
        } catch (error) {
            throw error;
        }
    };

    const handleGroupDelete = async (groupName) => {
        try {
            const groupToDelete = lockerGroups.find(g => g.groupName === groupName);
            if (!groupToDelete) return;

            // Delete all lockers in the group
            for (const locker of groupToDelete.lockers) {
                await deleteLockerAPI(locker.id);
            }

            // Update local state
            const remainingGroups = lockerGroups.filter(g => g.groupName !== groupName);
            setLockerGroups(remainingGroups);

            // Select first remaining group or reset
            if (remainingGroups.length > 0) {
                setSelectedGroup(remainingGroups[0].groupName);
                setLockers(remainingGroups[0].lockers);
            } else {
                setSelectedGroup('');
                setLockers([]);
            }
            setMode('view');
        } catch (error) {
            throw error;
        }
    };

    const refreshData = () => {
        fetchLockers();
        setMode('view');
    };

    useEffect(() => {
        fetchLockers();
    }, []);

    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
                fontSize: '18px',
                color: '#6c757d'
            }}>
                {t("lockers.loadingGroups")}...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
                gap: '16px'
            }}>
                <div style={{ fontSize: '18px', color: '#dc3545' }}>{error}</div>
                <button
                    onClick={refreshData}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                    }}
                >
                    {t("lockers.tryAgain")}
                </button>
            </div>
        );
    }

    if (lockerGroups.length === 0) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
                fontSize: '18px',
                color: '#6c757d'
            }}>
                {t("lockers.noGroupsFound")}
            </div>
        );
    }

    return (
        <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
            {/* Header with title, group selector and mode controls */}
            <div style={{
                position: 'fixed',
                top: '20px',
                left: '20px',
                zIndex: 200,
                backgroundColor: 'white',
                padding: '16px 20px',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                border: '2px solid #e9ecef',
                minWidth: '300px'
            }}>
                <h2 style={{
                    margin: '0 0 12px 0',
                    fontSize: '20px',
                    color: '#495057',
                    fontWeight: '600'
                }}>
                    {t("lockers.lockerGroups")}
                </h2>

                {/* Group selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <label style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#495057',
                        minWidth: '60px'
                    }}>
                        {t("lockers.group")}:
                    </label>
                    <select
                        value={selectedGroup}
                        onChange={handleGroupChange}
                        style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: '2px solid #e9ecef',
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            outline: 'none'
                        }}
                    >
                        <option value="all">{t("lockers.allLockers")}</option>
                        {lockerGroups.map(group => (
                            <option key={group.groupName} value={group.groupName}>
                                {group.groupName} ({group.lockers.length} {t("lockers.lockers")})
                            </option>
                        ))}
                    </select>
                </div>

                {/* Mode selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <label style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#495057',
                        minWidth: '60px'
                    }}>
                        {t("lockers.mode")}:
                    </label>
                    <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                        <button
                            onClick={() => handleModeChange('view')}
                            style={{
                                padding: '6px 12px',
                                backgroundColor: mode === 'view' ? '#007bff' : '#f8f9fa',
                                color: mode === 'view' ? 'white' : '#495057',
                                border: '1px solid #e9ecef',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                flex: 1
                            }}
                        >
                            {t("lockers.viewMode")}
                        </button>
                        <button
                            onClick={() => handleModeChange('edit')}
                            style={{
                                padding: '6px 12px',
                                backgroundColor: mode === 'edit' ? '#28a745' : '#f8f9fa',
                                color: mode === 'edit' ? 'white' : '#495057',
                                border: '1px solid #e9ecef',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                flex: 1
                            }}
                        >
                            {t("lockers.editMode")}
                        </button>
                    </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={refreshData}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            color: '#6c757d',
                            flex: 1
                        }}
                    >
                        {t("lockers.refreshData")}
                    </button>
                </div>


            </div>

            {/* LockerArrangement component */}
            <LockerArrangement
                loadedLockers={lockers}
                viewMode={mode === 'view'}
                editMode={mode === 'edit'}
                groupName={selectedGroup}
                onLockerUpdate={handleLockerUpdate}
                onLockerDelete={handleLockerDelete}
                onGroupDelete={handleGroupDelete}
            />
        </div>
    );
};

export default Lockers;